import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from '@headlessui/react';
import { toast } from 'react-hot-toast';

import { Modal } from '../../components/modal/Modal';
import { Button } from '../../components/buttons/Button';
import { InputTextArea } from '../../components/inputTextArea/InputTextArea';
import { rejectPreRegistration } from '../../redux/actions/preRegistration';

export const ModalCancelPreRegister = ({ open, setOpen, preRegisterId, onSuccess }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const cancelButtonRef = useRef(null);
	const { preRegisterSelected } = useSelector((state) => state.preRegistration);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [formData, setFormData] = useState({
		preRegisterId: preRegisterId,
		email: '',
		publicId: '',
		message: ''
	});

	useEffect(() => {
		setFormData((prevData) => ({
			...prevData,
			preRegisterId,
			message: '',
		}));
		setErrors({});
		setLoading(false);
	}, [open, preRegisterId]);

	useEffect(() => {
		setFormData((prevData) => ({
			...prevData,
			publicId: preRegisterSelected?.publicId || '',
			email: preRegisterSelected?.email || ''
		}));
		setLoading(false);
	}, [preRegisterSelected]);

	const onChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));

		if (value.trim() !== '' && errors[name]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: ''
			}));
		}
	};

	const validateForm = (updateState) => {
		const newErrors = {};

		if (!updateState.message || updateState.message.trim() === '') {
			newErrors.message = 'El mensaje es obligatorio para rechazar el preregistro.';
		} else if (updateState.message.trim().length < 10) {
			newErrors.message = 'El mensaje debe tener al menos 10 caracteres.';
		}

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			toast.error('Por favor, completa todos los campos requeridos.');
			return false;
		}

		return true;
	};

	const handleCancelPreRegister = async () => {
		const updateState = { ...formData, preRegisterId };
		const valid = validateForm(updateState);
		if (valid) {
			setLoading(true);
			dispatch(rejectPreRegistration(formData))
				.then((result) => {
					if (result.status === 200) {
						setOpen(false);
						setFormData((prevData) => ({
							...prevData,
							message: ''
						}));
						setLoading(false);
						if (typeof onSuccess === 'function') {
							onSuccess(result);
						} else {
							navigate('/candidatos');
						}
					} else {
						toast.error(result.message);
						setOpen(false);
						setLoading(false);
					}
				});
		}
	};

	return (
		<Modal open={open} setOpen={setOpen} cancelButtonRef={cancelButtonRef} autoCloseModal={false}>
			<div className="bg-white rounded-lg overflow-hidden">
				<div className="px-6 py-5 border-b border-gray-200">
					<div className="flex items-center gap-4">
						<div className="flex-shrink-0">
							<div className="w-12 h-12 bg-rose-500/20 rounded-full flex items-center justify-center backdrop-blur-sm border-2 border-rose-500/30">
								<svg className="w-6 h-6 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
								</svg>
							</div>
						</div>
						<div className="flex-1">
							<Dialog.Title as="h3" className="text-xl font-bold text-gray-900">
								Rechazar el registro de candidato
							</Dialog.Title>
							<p className="text-sm text-gray-900 mt-1">
								Esta acción notificará al estudiante
							</p>
						</div>
					</div>
				</div>

				<div className="px-6 py-6">
					<div className="space-y-2">
						<InputTextArea
							id={'message'}
							name={'message'}
							type={'message'}
							label={'Motivo del rechazo'}
							onChange={(e) => onChange(e)}
							value={formData.message}
							placeholder={'Ejemplo: El comprobante de pago no cumple con los requisitos. Por favor, explica con claridad qué debe corregir el candidato para volver a enviar su solicitud.'}
							error={errors.message}
						/>

						{errors.message && (
							<div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
								<svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
								</svg>
								<p className="text-sm text-red-700 font-medium">
									{errors.message}
								</p>
							</div>
						)}
					</div>
				</div>

				<div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
					<div className='flex flex-col-reverse sm:flex-row gap-3'>
						<Button
							type="button"
							variant="secondary"
							className="flex-1 sm:flex-initial border border-gray-300 shadow-sm"
							disabled={loading}
							onClick={() => setOpen(false)}
						>
							Cancelar
						</Button>
						<Button
							type="button"
							variant="dangerGradient"
							className="flex-1 py-2.5 px-6"
							loading={loading}
							loadingLabel="Procesando..."
							disabled={loading || formData.message.trim().length === 0}
							onClick={() => handleCancelPreRegister()}
						>
							<span className="flex items-center justify-center gap-2">
								<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
								</svg>
								Rechazar y notificar
							</span>
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
};
